import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Pill, Search, Plus, AlertTriangle, Eye, Edit, Trash2, X, Save } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

const MedicationProfilePage = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [medications, setMedications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMed, setEditingMed] = useState(null);
  const [formData, setFormData] = useState({
    name: '', dosage: '', frequency: '', route: 'Oral', prescriber: '', startDate: '', endDate: '', instructions: ''
  });

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      fetchMedications(selectedPatient.id);
    }
  }, [selectedPatient]);

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      if (response.data.success) {
        setPatients(response.data.patients);
        if (response.data.patients.length > 0) {
          setSelectedPatient(response.data.patients[0]);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMedications = async (patientId) => {
    try {
      const response = await api.get(`/medications/patient/${patientId}`);
      if (response.data.success) {
        setMedications(response.data.medications);
      }
    } catch (error) {
      console.error('Error fetching medications:', error);
      setMedications([]);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.dosage || !formData.frequency) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      const payload = { ...formData, patientId: selectedPatient.id };
      if (editingMed) {
        await api.put(`/medications/${editingMed.id}`, payload);
        toast.success('Medication updated');
      } else {
        await api.post('/medications', payload);
        toast.success('Medication added');
      }
      fetchMedications(selectedPatient.id);
      resetForm();
    } catch (error) {
      toast.error('Error saving medication');
    }
  };

  const handleDelete = async (medId) => {
    if (!window.confirm('Are you sure you want to delete this medication?')) return;
    try {
      await api.delete(`/medications/${medId}`);
      toast.success('Medication deleted');
      fetchMedications(selectedPatient.id);
    } catch (error) {
      toast.error('Error deleting medication');
    }
  };

  const startEdit = (med) => {
    setEditingMed(med);
    setFormData({
      name: med.name, dosage: med.dosage, frequency: med.frequency, route: med.route || 'Oral',
      prescriber: med.prescriber, startDate: med.startDate, endDate: med.endDate || '', instructions: med.instructions || ''
    });
    setShowAddForm(true);
  };

  const resetForm = () => {
    setShowAddForm(false);
    setEditingMed(null);
    setFormData({ name: '', dosage: '', frequency: '', route: 'Oral', prescriber: '', startDate: '', endDate: '', instructions: '' });
  };

  const filteredPatients = patients.filter(patient =>
    `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Pill className="w-6 h-6 text-[#f39c12]" />
                Medication Profile
              </h1>
              <p className="text-gray-500 text-sm mt-1">View and manage patient medication profiles</p>
            </div>
            {selectedPatient && (
              <Button onClick={() => setShowAddForm(true)} className="bg-[#f39c12] hover:bg-[#e67e22] text-white">
                <Plus className="w-4 h-4 mr-2" />Add Medication
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Patient List */}
          <div className="col-span-4">
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Search patients..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredPatients.map((patient) => (
                  <button
                    key={patient.id}
                    onClick={() => setSelectedPatient(patient)}
                    className={`w-full p-3 rounded-lg text-left transition-colors ${
                      selectedPatient?.id === patient.id ? 'bg-[#fff0e6] border-2 border-[#f39c12]' : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#3498db] rounded-full flex items-center justify-center text-white font-medium">
                        {patient.firstName?.[0]}{patient.lastName?.[0]}
                      </div>
                      <div>
                        <div className="font-medium">{patient.firstName} {patient.lastName}</div>
                        <div className="text-xs text-gray-500">{medications.length} medications</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Medication Profile */}
          <div className="col-span-8">
            {showAddForm ? (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold">{editingMed ? 'Edit Medication' : 'Add New Medication'}</h2>
                  <Button variant="ghost" onClick={resetForm}><X className="w-5 h-5" /></Button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Medication Name *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="e.g., Metformin 500mg" className="mt-1" /></div>
                  <div><Label>Dosage *</Label><Input value={formData.dosage} onChange={(e) => setFormData({...formData, dosage: e.target.value})} placeholder="e.g., 1 tablet" className="mt-1" /></div>
                  <div><Label>Frequency *</Label><Input value={formData.frequency} onChange={(e) => setFormData({...formData, frequency: e.target.value})} placeholder="e.g., Twice daily with meals" className="mt-1" /></div>
                  <div><Label>Route</Label>
                    <select value={formData.route} onChange={(e) => setFormData({...formData, route: e.target.value})} className="w-full mt-1 p-2 border rounded">
                      <option>Oral</option><option>IV</option><option>IM</option><option>SQ</option><option>Topical</option><option>Inhaled</option>
                    </select>
                  </div>
                  <div><Label>Prescriber</Label><Input value={formData.prescriber} onChange={(e) => setFormData({...formData, prescriber: e.target.value})} placeholder="Dr. Johnson" className="mt-1" /></div>
                  <div><Label>Start Date</Label><Input type="date" value={formData.startDate} onChange={(e) => setFormData({...formData, startDate: e.target.value})} className="mt-1" /></div>
                  <div className="col-span-2"><Label>Instructions</Label><Input value={formData.instructions} onChange={(e) => setFormData({...formData, instructions: e.target.value})} placeholder="Take with food" className="mt-1" /></div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <Button variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button onClick={handleSubmit} className="bg-[#f39c12] hover:bg-[#e67e22] text-white"><Save className="w-4 h-4 mr-2" />Save</Button>
                </div>
              </div>
            ) : selectedPatient ? (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center gap-4 mb-6 pb-4 border-b">
                  <div className="w-16 h-16 bg-[#3498db] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {selectedPatient.firstName?.[0]}{selectedPatient.lastName?.[0]}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{selectedPatient.firstName} {selectedPatient.lastName}</h2>
                    <p className="text-gray-500">Primary Diagnosis: {selectedPatient.primaryDiagnosis || 'Not specified'}</p>
                  </div>
                </div>

                <h3 className="font-semibold text-lg mb-4">Active Medications ({medications.length})</h3>
                {medications.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Pill className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No medications recorded for this patient</p>
                    <Button onClick={() => setShowAddForm(true)} className="mt-4 bg-[#f39c12] hover:bg-[#e67e22] text-white">
                      <Plus className="w-4 h-4 mr-2" />Add First Medication
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {medications.map((med) => (
                      <div key={med.id} className="p-4 border rounded-lg hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <Pill className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold">{med.name}</h4>
                              <p className="text-sm text-gray-600">{med.dosage} - {med.frequency}</p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                <span>Route: {med.route}</span>
                                <span>Start: {med.startDate}</span>
                                {med.prescriber && <span>{med.prescriber}</span>}
                              </div>
                              {med.instructions && <p className="text-xs text-gray-500 mt-1">Instructions: {med.instructions}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded-full text-xs ${med.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{med.status}</span>
                            <Button variant="ghost" size="sm" onClick={() => startEdit(med)}><Edit className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(med.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-yellow-800">Drug Interaction Check</h4>
                      <p className="text-sm text-yellow-700">No major interactions detected</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border p-8 text-center text-gray-500">
                Select a patient to view their medication profile
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MedicationProfilePage;
